import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

import {
  GuardrailComposition,
  UpdateGuardrailCompositionNewImageBody,
  UpdateGuardrailCompositionRemoveImageBody,
} from "../../../../../http/border/types/guardrailCompositions";
import { useGuardrailCompositionsApi } from "../../../../../http/border/useGuardrailCompositions";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import TranslatableField from "../../../../ui/TranslatableField";

interface UpdateGuardrailCompositionDetailsProps {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  isIntermediateBarRequired: boolean;
  isToeBoardRequired: boolean;
}

interface GuardrailCompositionFormProps {
  guardrailComposition?: GuardrailComposition;
  disabled?: boolean;
}

const GuardrailCompositionForm: FC<GuardrailCompositionFormProps> = ({
  guardrailComposition,
  disabled,
}: GuardrailCompositionFormProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const guardrailCompositionsApi = useGuardrailCompositionsApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: guardrailComposition?.image ?? undefined,
    isLoading: !guardrailComposition || (!!guardrailComposition && !guardrailComposition?.id),
    disabled,
  });

  const defaultValues: UpdateGuardrailCompositionDetailsProps = {
    name: guardrailComposition?.name ?? null,
    nameIt: guardrailComposition?.nameIt ?? null,
    nameDe: guardrailComposition?.nameDe ?? null,
    nameEs: guardrailComposition?.nameEs ?? null,
    nameFr: guardrailComposition?.nameFr ?? null,
    order: guardrailComposition?.order ?? 50,
    visibility: guardrailComposition?.visibility ?? "hidden",
    isIntermediateBarRequired: guardrailComposition?.isIntermediateBarRequired ?? false,
    isToeBoardRequired: guardrailComposition?.isToeBoardRequired ?? false,
  };

  const submit = async (body: UpdateGuardrailCompositionNewImageBody | UpdateGuardrailCompositionRemoveImageBody) => {
    if (guardrailComposition) {
      try {
        await guardrailCompositionsApi.updateAdminGuardrailComposition(guardrailComposition.id, body);
        dispatch(showSuccessNotification(i18n.translation));
        onSave();
      } catch (err) {
        return dispatch(showErrorNotification(i18n, err));
      } finally {
        return setIsSubmitting(false);
      }
    }
  };

  const onSubmit = () => async (formData: UpdateGuardrailCompositionDetailsProps) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      isIntermediateBarRequired: formData?.isIntermediateBarRequired ?? false,
      isToeBoardRequired: formData?.isToeBoardRequired ?? false,
    };

    if (newImage && guardrailComposition) {
      try {
        const signedUrl = await guardrailCompositionsApi.generateUploadImageUrl(
          guardrailComposition.id,
          newImage.name,
          newImage.type,
        );
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateGuardrailCompositionNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateGuardrailCompositionRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateGuardrailCompositionDetailsProps>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        const isIntermediateBarRequired = !!watch("isIntermediateBarRequired");
        const isToeBoardRequired = !!watch("isToeBoardRequired");

        useEffect(() => {
          reset(defaultValues);
        }, [guardrailComposition]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <TranslatableField
                  field="name"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.name}
                >
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>

            <FormField label={i18n.translation.common.isIntermediateBarRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={isIntermediateBarRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("isIntermediateBarRequired", checked)}
                />
              )}
            </FormField>

            <FormField label={i18n.translation.common.isToeBoardRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={isToeBoardRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("isToeBoardRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={disabled}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={disabled}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default GuardrailCompositionForm;
